import GLOBALS from './GLOBALS'
import {Token} from "./common";
import store from "../redux/store";
import {setAuthError, setAuthLoading, setSignedIn} from "../redux/actions/authActions";
import {redirectTo} from "../routes/functions";
import {Urls} from "./urls";
import API from "./api";
import {enqueueSnackbar} from "../redux/actions/snackbarActions";
import {errorMessage, successMessage} from "./alertMessages";
import snackbarMessages from "./snackbarMessages";
import { InstitutionService } from "./InstitutionService";
import { PortalUsersService } from "./PortalUsersService";
import { setEvaluatorStatus, setUserRoles } from "../redux/actions/authActions";
import { resetRouteState } from "../redux/store/routeSlice";
import { UserFlowService, USER_FLOW_STATES } from "./UserFlowService";


// Helper function to handle successful institution setup and redirect
const handleSuccessfulInstitutionSetup = (response, institutionData) => {
  // Store institution data in sessionStorage
  sessionStorage.setItem(GLOBALS.SESSION_KEYS.SELECTED_INSTITUTION_ID, institutionData.keyid);
  sessionStorage.setItem(GLOBALS.SESSION_KEYS.SELECTED_INSTITUTION_NAME, institutionData.institution_name);
  sessionStorage.setItem(GLOBALS.SESSION_KEYS.INSTITUTION_COUNTRY, JSON.stringify(institutionData.country));
  
  const roles = response.data.roles;
  
  // Get evaluator_status from response or sessionStorage (stored by PortalUsersService.setInstitution)
  const evaluatorStatus = response.data.evaluator_status || 
                          JSON.parse(sessionStorage.getItem(GLOBALS.SESSION_KEYS.EVALUATOR_STATUS) || '"0"');
  
  // Handle different redirect scenarios based on user roles and status
  if (response.data.agree_tandc === 0) {
    // User needs to accept terms
    UserFlowService.setUserFlowState(USER_FLOW_STATES.TERMS_PENDING);
    
    // Store state for terms page
    const stateData = { 
      id: institutionData.keyid, 
      title: institutionData.institution_name, 
      country: institutionData.country 
    };
    sessionStorage.setItem('redirect_state', JSON.stringify(stateData));
    // DON'T dispatch setSignedIn yet - let Terms and Conditions page handle it
    redirectTo("/onboarding/terms_and_conditions");
    return;
  }
  
  // Special case: evaluator with status 1 should go to training (highest priority after terms)
  const isEvaluatorWithStatus1 = roles?.includes('evaluator') && 
                                 (evaluatorStatus === 1 || evaluatorStatus === "1");
  
  if (isEvaluatorWithStatus1) {
    // Evaluator with status 1 goes to training regardless of other roles
    const stateData = { 
      institution_id: institutionData.keyid, 
      title: institutionData.institution_name 
    };
    sessionStorage.setItem('redirect_state', JSON.stringify(stateData));
    if (UserFlowService.needsEvaluatorOnboarding()) {
      UserFlowService.setUserFlowState(USER_FLOW_STATES.ONBOARDING_PENDING);
      redirectTo("/onboarding/unified");
    } else {
      UserFlowService.setUserFlowState(USER_FLOW_STATES.COMPLETED);
      store.dispatch(setSignedIn());
      redirectTo("/training");
    }
    return;
  }
  
  // Priority order: clinician/institutional_clinician > user_manager > evaluator > medical_record_entry
  if (roles?.includes('clinician') || roles?.includes('institutional_clinician')) {
    // Clinician needs onboarding for patient reports information
    const stateData = { 
      institution_id: institutionData.keyid, 
      title: institutionData.institution_name 
    };
    sessionStorage.setItem('redirect_state', JSON.stringify(stateData));
    if (UserFlowService.needsClinicianOnboarding()) {
      // Clinician needs onboarding - use unified onboarding
      UserFlowService.setUserFlowState(USER_FLOW_STATES.ONBOARDING_PENDING);
      redirectTo("/onboarding/unified");
    } else {
      // Onboarding completed - redirect to patients
      UserFlowService.setUserFlowState(USER_FLOW_STATES.COMPLETED);
      store.dispatch(setSignedIn());
      redirectTo("/patients");
    }
  } else if (roles?.includes('user_manager')) {
    // Check if user manager has completed onboarding
    const stateData = { 
      id: institutionData.keyid, 
      title: institutionData.institution_name 
    };
    sessionStorage.setItem('redirect_state', JSON.stringify(stateData));
    if (UserFlowService.needsOnboarding()) {
      // User needs onboarding - use unified onboarding
      UserFlowService.setUserFlowState(USER_FLOW_STATES.ONBOARDING_PENDING);
      redirectTo("/onboarding/unified");
    } else {
      // Onboarding completed - user can access dashboard
      UserFlowService.setUserFlowState(USER_FLOW_STATES.COMPLETED);
      store.dispatch(setSignedIn());
      redirectTo("/users");
    }
  } else if (roles?.includes('evaluator')) {
    // Evaluator needs onboarding for training information
    const stateData = { 
      institution_id: institutionData.keyid, 
      title: institutionData.institution_name 
    };
    sessionStorage.setItem('redirect_state', JSON.stringify(stateData));
    if (UserFlowService.needsEvaluatorOnboarding()) {
      // Evaluator needs onboarding - use unified onboarding
      UserFlowService.setUserFlowState(USER_FLOW_STATES.ONBOARDING_PENDING);
      redirectTo("/onboarding/unified");
    } else {
      // Onboarding completed - redirect to videos/training
      UserFlowService.setUserFlowState(USER_FLOW_STATES.COMPLETED);
      store.dispatch(setSignedIn());
      redirectTo("/training");
    }
  } else if (roles?.includes('medical_record_entry')) {
    // No additional flow needed - user can access dashboard
    UserFlowService.setUserFlowState(USER_FLOW_STATES.COMPLETED);
    store.dispatch(setSignedIn());
    const stateData = { 
      id: institutionData.keyid, 
      title: institutionData.institution_name 
    };
    sessionStorage.setItem('redirect_state', JSON.stringify(stateData));
    redirectTo("/medical_records/list");
  } else {
    // Default fallback - redirect to patients
    UserFlowService.setUserFlowState(USER_FLOW_STATES.COMPLETED);
    store.dispatch(setSignedIn());
    const stateData = { 
      institution_id: institutionData.keyid, 
      title: institutionData.institution_name 
    };
    sessionStorage.setItem('redirect_state', JSON.stringify(stateData));
    redirectTo("/patients");
  }
};

// Helper function to handle institution list and auto-selection
const handleInstitutionList = async (dispatch) => {
  try {
    const response = await InstitutionService.list_own(dispatch);
    
    if (response?.data?.data) {
      // Cache the institution list for potential reuse
      sessionStorage.setItem('cached_institutions', JSON.stringify(response.data.data));
      sessionStorage.setItem('cached_institutions_timestamp', Date.now().toString());
      
      // Filter out the institution with keyid 0 (Admin institution) and disabled/distribution sites
      const filteredInstitutions = response.data.data.filter(
        (i) => i.keyid !== 0 && i?.distribution_site !== 1 && i?.enabled === 1
      );
      
      // If there's only one institution, automatically set it
      if (filteredInstitutions.length === 1) {
        const institution = filteredInstitutions[0];
        
        try {
          const setInstResponse = await PortalUsersService.setInstitution(institution.keyid);
          
          // Handle MFA scenario - redirect to institutions page for MFA handling
          if (setInstResponse?.data?.mfa_result) {
            store.dispatch(setSignedIn());
            redirectTo('/institutions?from=/auth/sign-in&auto_select=' + institution.keyid);
            return;
          }
          
          // Handle successful institution setup
          if (setInstResponse !== null && setInstResponse?.data) {
            handleSuccessfulInstitutionSetup(setInstResponse, institution);
            return;
          }
          
          // Handle error case - redirect to institutions page
          store.dispatch(setSignedIn());
          redirectTo('/institutions?from=/auth/sign-in');
          return;
          
        } catch (error) {
          console.error('Error setting institution during login:', error);
          store.dispatch(setSignedIn());
          redirectTo('/institutions?from=/auth/sign-in');
          return;
        }
      }
      
      // Multiple institutions or no institutions - redirect to institutions page
      store.dispatch(setSignedIn());
      redirectTo('/institutions?from=/auth/sign-in');
      return;
    }
    
    // Error getting institution list
    store.dispatch(setSignedIn());
    redirectTo('/institutions?from=/auth/sign-in');
    
  } catch (error) {
    console.error('Error getting institution list during login:', error);
    store.dispatch(setSignedIn());
    redirectTo('/institutions?from=/auth/sign-in');
  }
};

export const AuthService = {

  attemptLogin: function () {
    const username = document.getElementById("email").value;
    const password = document.getElementById("password").value;
    AuthService.login(username,password);
  },

  login: function (username,password) {
    sessionStorage.clear();
    store.dispatch(setAuthLoading())
    let xhr = new XMLHttpRequest();
    xhr.open('POST', Urls.api, true);

    xhr.setRequestHeader(GLOBALS.HEADERS_NAMES.AUTH_TYPE,
                         GLOBALS.HEADERS_VALUES.LOGIN);

    xhr.setRequestHeader(GLOBALS.HEADERS_NAMES.AUTHORIZATION,
                         GLOBALS.HEADERS_VALUES.BASIC + " " + btoa(username + ":"  + password));

    xhr.setRequestHeader(GLOBALS.HEADERS_NAMES.LOGIN_TYPE,
                         "1");

    xhr.responseType = "json";

    xhr.onreadystatechange = function() {
        if (xhr.readyState === 4) {
           if (this.response?.message === "OK"){
              Token.setToken(this.response.data["token"]);
              sessionStorage.setItem(GLOBALS.SESSION_KEYS.USER,username);
              sessionStorage.setItem(GLOBALS.SESSION_KEYS.USER_ID,this.response.data["id"]);
              sessionStorage.setItem(GLOBALS.SESSION_KEYS.USER_FNAME,this.response.data["fname"]);
              sessionStorage.setItem(GLOBALS.SESSION_KEYS.USER_LNAME,this.response.data["lname"]);

              // Handle institution selection during login (setSignedIn will be called later)
              handleInstitutionList(store.dispatch);
           }
           else{
              store.dispatch(setAuthError(this.response));
              throw this.response
           }
        }
    };

    xhr.send(null);

  },
  logout: function () {
        let endpoint = GLOBALS.ENDPOINTS.PORTAL_USER.LOGOUT + "/0"// the identifier here, is ignored.
        sessionStorage.clear();
        // Clear user flow state
        UserFlowService.clearUserFlowState();
        return API.post(endpoint).then((response) => {
          return response
        })
            .catch((error) => {
              redirectTo('/auth/sign-in')
            })
    },
  receivedInstitutionInfo: function(data){

    //console.log(JSON.stringify(data));

    if (data.message !== "OK"){
        document.getElementById("error_message").innerHTML = data.message;
       // WaitDialog.close();
        return;
    }

    let ilist = data.data;
    let inst_list = [];
    for (let i = 0; i < ilist.length; i++){
        let inst = {};
        inst[GLOBALS.INST_LIST.NAME] =  ilist[i]["institution_name"];
        inst[GLOBALS.INST_LIST.ID]   =  ilist[i]["keyid"];
        inst_list.push(inst);
    }

    sessionStorage.setItem(GLOBALS.SESSION_KEYS.USER_INSTITUTIONS,JSON.stringify(inst_list));

    // Making sure we are home.
   // WaitDialog.close();
    window.location.href = "index.html"

  },
  modifyAccount: function(params){
    let endpoint = GLOBALS.ENDPOINTS.PORTAL_USER.MODIFY_OWN +
      "/" + sessionStorage.getItem(GLOBALS.SESSION_KEYS.USER_ID);

    //filtering params to send only updated ones
    const filtered = Object.fromEntries(Object.entries(params).filter(([k,v]) => v!==null));

    return API.post(endpoint, filtered).then((response) => {
      store.dispatch(enqueueSnackbar(successMessage(snackbarMessages.success.account.post)));
      if(filtered['name']){
        sessionStorage.setItem(GLOBALS.SESSION_KEYS.USER_FNAME,filtered['name']);
      }
      if(filtered['lastname']){
        sessionStorage.setItem(GLOBALS.SESSION_KEYS.USER_LNAME,filtered['lastname']);
      }
      redirectTo("/institutions")
      return response
    }).catch((error) => {
        store.dispatch(enqueueSnackbar(errorMessage(snackbarMessages.error.account.post)))
      })
  },
  sendResetPasswordEmail: function(email){
    let endpoint = GLOBALS.ENDPOINTS.PORTAL_USER.RESET_PASSWORD + "/" + email;
    return API.post(endpoint).then((response) => {
      store.dispatch(enqueueSnackbar(successMessage(snackbarMessages.success.account.resetPassword)));
      return response.data.message
    }).catch((error) => {
        store.dispatch(enqueueSnackbar(errorMessage(snackbarMessages.error.account.resetPassword)))
        return error.response.data.message
      })
    },
    resetPassword: function(id,token,password, qr = false){
      let endpoint = GLOBALS.ENDPOINTS.PORTAL_USER.RESET_PASSWORD + "/" + id + "?key=" + token;
      if (qr) {
        endpoint += "&qr=1";
      }
      return API.post(endpoint, {password: password}).then((response) => {
        return response.data
      }).catch((error) => {
          store.dispatch(enqueueSnackbar(errorMessage(snackbarMessages.error.account.resetPassword)))
          return error.response.data
        })
    },

    // New method to send 2FA setup email
    send2FASetupEmail: function(email){
      let endpoint = GLOBALS.ENDPOINTS.PORTAL_USER.RESET_PASSWORD + "/" + email + "?qr=1";
      return API.post(endpoint).then((response) => {
        return response.data
      }).catch((error) => {
        return error.response.data
      })
    }
}

